import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axiosInstance";
import { businessScopeParams } from "../utils/businessScope";
import { SHIPPING_SETTINGS_PATH, itemShipping, formatKg } from "../utils/shippingStatus";

/**
 * The order page's parts (eventseasyadmin#299) and the shipping status both
 * order pages show. Styling: AdminTheme.css .mtk-order-*.
 *
 * A courier store (Delhivery) books, labels, schedules pickup and follows
 * each parcel through uniquegiftapi's shipment APIs (#298 APIs 7-11):
 *   POST /shipping/orders/:orderId/shipment   { package }
 *   GET  /shipping/orders/:orderId/label
 *   POST /shipping/orders/:orderId/pickup     { date, slot }
 *   POST /shipping/orders/:orderId/cancel
 *   GET  /shipping/orders/:orderId/tracking
 * The order's `shipment` holds the AWB, status and tracking updates. A Manual / self-ship store adds the courier and tracking
 * number itself and moves the status on (PUT /event/update-delivery-status).
 */

/**
 * "#298D34": the last 6 characters of this order's own id, on the list and its
 * page alike (order_id is shared by every store's part of one checkout)
 */
export const orderNumber = (id) => String(id || "").slice(-6).toUpperCase();

/**
 * Logged in as a store (signup "user"). The courier actions need the store's
 * seller session (X-Seller-Token): a Super Admin calling them gets a 401 that
 * logs them out, so they see these orders read-only.
 */
export const isSellerSession = () => {
    try {
        return localStorage.getItem("signup") === "user";
    } catch (e) {
        return false;
    }
};

/**
 * Which shipping panel an order gets:
 * - "booked" / "moving": a live courier shipment (before / after pickup)
 * - "toShip": a courier store's order still to ship (book it)
 * - "manual": anything else not cancelled - a self-ship store's orders, and a
 *   courier store's orders already sent another way (shipped, delivered or
 *   returned before it connected the courier): their status is updated by hand
 * - "readonly": a courier order seen by a Super Admin
 * - "none": cancelled
 */
export const panelFor = (order, courierStore, seller = isSellerSession()) => {
    const status = order.current_tracking_status || "ordered";
    if (order.purchase_status === "cancelled" || status === "cancelled") return "none";
    if (hasShipment(order)) {
        if (!seller) return "readonly";
        return ["CREATED", "PICKUP_SCHEDULED"].includes(shipmentOf(order).status) ? "booked" : "moving";
    }
    if (courierStore && (status === "ordered" || status === "packed")) return seller ? "toShip" : "readonly";
    return "manual";
};

const DAY = 86400000;

const date = (value) => {
    const d = value ? new Date(value) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
};

export const formatPlaced = (value) => {
    const d = date(value);
    return d ? d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "";
};
// "Tue, 4 Nov" (en-GB alone leaves out the comma)
const dayLabel = (d, timeZone) => `${d.toLocaleDateString("en-GB", { weekday: "short", timeZone })}, ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone })}`;
export const formatDay = (value) => {
    const d = date(value);
    return d ? dayLabel(d) : "";
};
export const formatShort = (value) => {
    const d = date(value);
    return d ? d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "";
};

export const rupees = (amount) => `₹${(Number(amount) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const isCod = (order) => String(order.payment_mode || "").toLowerCase() === "cod";

const shipmentOf = (order) => (order && order.shipment) || {};

/** The order has a live courier shipment (an AWB, not cancelled) */
export const hasShipment = (order) => {
    const s = shipmentOf(order);
    return !!s.awb && s.status !== "CANCELLED" && s.status !== "CLOSED";
};

// No courier update for this long: the seller may stop following the parcel
// (uniquegiftapi shipments.STALE_DAYS, which the API checks)
export const STALE_DAYS = 7;

/** Whole days since the courier last updated a live shipment, or 0 */
export const daysSinceCourierUpdate = (order, now = Date.now()) => {
    const s = shipmentOf(order);
    const times = (s.events || []).filter((e) => e && e.by !== "seller").map((e) => new Date(e.time).getTime());
    times.push(new Date(s.created_at || 0).getTime());
    const latest = Math.max(...times.filter((n) => !Number.isNaN(n)));
    return latest > 0 ? Math.floor((now - latest) / DAY) : 0;
};

export const PICKUP_SLOTS = { "10-14": "10 AM – 2 PM", "14-18": "2 PM – 6 PM" };

const COURIER_CHIPS = {
    CREATED: { label: "Pickup pending", tone: "purple" },
    PICKUP_SCHEDULED: { label: "Pickup scheduled", tone: "purple" },
    PICKED_UP: { label: "Picked up", tone: "blue" },
    IN_TRANSIT: { label: "In transit", tone: "blue" },
    OUT_FOR_DELIVERY: { label: "Out for delivery", tone: "amber" },
    FAILED_DELIVERY: { label: "Delivery failed", tone: "red" },
    DELIVERED: { label: "Delivered", tone: "green" },
    RTO_INITIATED: { label: "RTO initiated", tone: "red" },
    RTO_DELIVERED: { label: "Returned to you", tone: "returned" },
};

const MANUAL_CHIPS = {
    shipped: { label: "Shipped", tone: "blue" },
    in_transit: { label: "In transit", tone: "blue" },
    out_for_delivery: { label: "Out for delivery", tone: "amber" },
    failed_delivery: { label: "Delivery failed", tone: "red" },
    delivered: { label: "Delivered", tone: "green" },
    returned: { label: "Returned", tone: "returned" },
};

// The courier couldn't book it and the seller must act (not "still retrying")
const needsAction = (s) => ["auth", "rejected"].includes(s.last_error_code)
    || (s.last_error_code === "unavailable" && !s.next_retry_at);

/** The shipping status chip of an order, on its page and in the list */
export const shippingChip = (order) => {
    const status = order.current_tracking_status || "ordered";
    if (order.purchase_status === "cancelled" || status === "cancelled") return { label: "Cancelled", tone: "red" };
    const s = shipmentOf(order);
    if (hasShipment(order) && COURIER_CHIPS[s.status]) return COURIER_CHIPS[s.status];
    if (status === "ordered" || status === "packed") {
        return needsAction(s) ? { label: "To ship · action needed", tone: "red" } : { label: "To ship", tone: "neutral" };
    }
    return MANUAL_CHIPS[status] || { label: "To ship", tone: "neutral" };
};

/** Which Order Management tab an order is under */
export const orderTab = (order) => {
    const status = order.current_tracking_status || "ordered";
    if (order.purchase_status === "cancelled" || status === "cancelled") return "cancelled";
    const s = shipmentOf(order);
    if (status === "returned" || (hasShipment(order) && /^RTO_/.test(s.status))) return "returned";
    if (status === "delivered") return "delivered";
    if (!hasShipment(order) && (status === "ordered" || status === "packed")) return "toShip";
    return "shipped";
};

/**
 * What needs the seller now, or null: a shipment the courier couldn't book,
 * a failed delivery, a returned prepaid order to refund, a self-shipped
 * parcel with no update for 4 days. { tone, text }
 */
export const orderAttention = (order, now = Date.now()) => {
    const s = shipmentOf(order);
    const tab = orderTab(order);
    // The courier is still moving an order the seller cancelled
    if (s.conflict) return { tone: "red", text: s.conflict };
    const courier = s.provider === "delhivery" ? "Delhivery" : "The courier";
    if (tab === "toShip" && s.last_error_code === "auth") {
        return { tone: "red", text: `Couldn't create the shipment – ${courier} rejected your saved API token. Update it in Shipping Settings.` };
    }
    if (tab === "toShip" && s.last_error_code === "unavailable") {
        return s.next_retry_at
            ? { tone: "red", text: `Couldn't create the shipment – ${courier} didn't respond. Retrying automatically (attempt ${(s.attempts || 0) + 1} of 5).` }
            : { tone: "red", text: `Couldn't create the shipment – ${courier} didn't respond after 5 tries. Try again.` };
    }
    if (tab === "toShip" && s.last_error_code === "rejected") return { tone: "red", text: `Couldn't create the shipment – ${rejectionHelp(s.last_error).reason}` };
    // Stopped before reaching the courier: the address, the package, the order
    if (tab === "toShip" && s.last_error_code && s.last_error) return { tone: "red", text: `Couldn't create the shipment – ${s.last_error}` };
    const failed = s.status === "FAILED_DELIVERY" || order.current_tracking_status === "failed_delivery";
    if (failed && tab === "shipped") return { tone: "amber", text: "Call the customer before the next attempt, or the parcel will go back to you (RTO)." };
    if (tab === "returned" && !isCod(order) && order.payment_status === "paid") {
        return { tone: "amber", text: "Paid online – refund this customer yourself (TamilKart does not handle refunds)." };
    }
    if (tab === "shipped" && !hasShipment(order)) {
        const last = date(order.updated_date) || date(order.shipped_on);
        const days = last ? Math.floor((now - last.getTime()) / DAY) : 0;
        if (days >= 4) return { tone: "amber", text: `No update for ${days} days – check with the courier and update the status.` };
    }
    return null;
};

/** The small line under the status chip in the list (courierStore: the store ships with a courier) */
export const statusDetail = (order, courierStore) => {
    const s = shipmentOf(order);
    const tab = orderTab(order);
    if (tab === "toShip") return courierStore ? "No shipment yet" : `Ordered ${formatShort(order.created_date)}`;
    if (tab === "delivered") return formatShort(order.actual_delivery_date);
    if (hasShipment(order)) {
        if (s.status === "PICKUP_SCHEDULED" && s.pickup) return `Pickup ${formatShort(s.pickup.date).split(",")[0]}, ${PICKUP_SLOTS[s.pickup.slot] || ""}`.trim();
        if (s.status === "CREATED") return "Label and pickup pending";
        if (s.status === "OUT_FOR_DELIVERY") return "Today";
        if (["FAILED_DELIVERY", "RTO_INITIATED", "RTO_DELIVERED"].includes(s.status)) return s.status_detail || "";
    }
    const eta = date(s.expected_delivery) || date(order.estimated_delivery_date);
    return eta ? `Est. ${eta.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : "";
};

/** Shipment steps: the courier's, or a self-shipped order's */
const COURIER_STEPS = ["Placed", "Shipment created", "Picked up", "In transit", "Delivered"];
const MANUAL_STEPS = ["Placed", "Shipped", "In transit", "Out for delivery", "Delivered"];

const COURIER_STEP = { CREATED: 1, PICKUP_SCHEDULED: 1, PICKED_UP: 2, IN_TRANSIT: 3, OUT_FOR_DELIVERY: 3, FAILED_DELIVERY: 3, DELIVERED: 4, RTO_INITIATED: 3, RTO_DELIVERED: 3 };
const MANUAL_STEP = { shipped: 1, in_transit: 2, out_for_delivery: 3, failed_delivery: 3, delivered: 4, returned: 3 };

/** { steps, step }: how far along the order is */
export const progressFor = (order, courier) => {
    if (courier) return { steps: COURIER_STEPS, step: hasShipment(order) ? COURIER_STEP[shipmentOf(order).status] || 0 : 0 };
    return { steps: MANUAL_STEPS, step: MANUAL_STEP[order.current_tracking_status] || 0 };
};

/** The ordered products (gift/list results with orderCount, selected_variants) as order lines */
export const orderLines = (items) => (items || []).map((item) => {
    const { kg, box } = itemShipping(item, item.selected_variants);
    return {
        id: item._id,
        name: item.name || "",
        url: item.url || "",
        qty: Number(item.orderCount) || 1,
        price: Number(item.price) || 0,
        kg,
        dims: box || {},
        variants: item.selected_variants || [],
    };
});

export const orderTotals = (order, lines) => {
    const items = lines.reduce((sum, line) => sum + line.price * line.qty, 0);
    const shipping = Number(order.shipping_charge) || 0;
    const paid = Number(order.purchased_price);
    return { items, shipping, total: paid > 0 ? paid : items + shipping };
};

/**
 * The package from the products' saved sizes, as the API works it out
 * (uniquegiftapi productShipping.packageFor): the total weight when every
 * product has one, and a box that holds every unit laid flat and stacked -
 * the largest long and middle sides, the shortest sides added up.
 */
export const packageFrom = (lines) => {
    const allWeighed = lines.length > 0 && lines.every((line) => line.kg != null);
    let length = 0;
    let width = 0;
    let height = 0;
    lines.forEach((line) => {
        const d = line.dims || {};
        const sides = [Number(d.length), Number(d.width), Number(d.height)];
        if (!sides.every((n) => n > 0)) return;
        const [longest, middle, shortest] = sides.sort((a, b) => b - a);
        length = Math.max(length, longest);
        width = Math.max(width, middle);
        height += shortest * (line.qty || 1);
    });
    const cm = (n) => (n > 0 ? String(Math.round(n * 10) / 10) : "");
    return {
        weight: allWeighed ? formatKg(lines.reduce((sum, line) => sum + line.kg * line.qty, 0)) : "",
        length: cm(length),
        width: cm(width),
        height: cm(height),
    };
};

export const customerAddress = (order) => {
    const shippingTo = [order.shipping_address, order.shipping_city, order.shipping_state].filter(Boolean).join(", ");
    if (shippingTo) return [shippingTo, order.shipping_zip_code].filter(Boolean).join(" ");
    return [order.address, order.zip_code].filter(Boolean).join(" ");
};

/**
 * A courier's refusal as the seller should read it: the reason (Delhivery
 * wraps it as "Crashing while saving package due to exception '...'"), what to
 * do about it, and whether a half-made shipment may be left in the courier
 * account ("Package might have been partially saved").
 */
export const rejectionHelp = (text, providerLabel = "Delhivery") => {
    const raw = String(text || "");
    const inner = raw.match(/exception '([^']+)'/i);
    const reason = (inner ? inner[1] : raw).trim();
    const partial = /partially saved/i.test(raw);
    let advice = "Check the customer's address and the package, then try again.";
    if (/clientwarehouse|warehouse|pickup location/i.test(raw)) {
        advice = `Your pickup location name doesn't match a pickup location in your ${providerLabel} account. Copy its Facility Name exactly into Shipping Settings.`;
    } else if (/serviceab/i.test(raw)) {
        advice = `${providerLabel} doesn't deliver to this pincode from your account. Ship this order yourself, or ask ${providerLabel} to enable the pincode.`;
    } else if (/insufficient balance|wallet|recharge/i.test(raw)) {
        advice = `Your ${providerLabel} wallet balance is too low. Recharge it in your ${providerLabel} account, then try again.`;
    } else if (/duplicate|already exist/i.test(raw)) {
        advice = `${providerLabel} already has a shipment for this order. Check your ${providerLabel} account before trying again.`;
    }
    return { reason, advice, partial };
};

const apiError = (error, fallback) => {
    const data = (error && error.response && error.response.data) || {};
    return data.message || (data.error && data.error.message) || fallback;
};

// Booking can take about 35 s at worst (create, then a lookup if it got no reply)
export const SHIPMENT_TIMEOUT = 60000;

/** The order's shipment API path (#298 APIs 7-11) */
export const shipmentApi = (orderId, action) => `/shipping/orders/${encodeURIComponent(orderId)}/${action}`;

/**
 * The label PDF link of each order (GET /shipping/orders/:orderId/label):
 * [{ id, url } | { id, error }], one per order, in order.
 */
export const fetchLabels = async (eventIds) => {
    const results = [];
    for (const id of eventIds) {
        try {
            const res = await api.get(shipmentApi(id, "label"));
            const url = res.data && res.data.result && res.data.result.url;
            results.push(url ? { id, url } : { id, error: "The courier has no label for this shipment yet. Try again in a minute." });
        } catch (error) {
            results.push({ id, error: apiError(error, "Couldn't get the label. Please try again.") });
        }
    }
    return results;
};

/** Only an https label link goes into a tab (the API checks too) */
const safeLabelUrl = (url) => (/^https:\/\//i.test(String(url || "")) ? url : "");

/**
 * Opens one order's label in a new tab. Browsers allow a new tab only during
 * the click itself, not after the label request returns, so call this from the
 * click handler: the tab opens at once and goes to the label when it arrives
 * (or closes again if there is none). Throws the reason there's no label.
 * @returns {Promise<{url: string, blocked: boolean}>} blocked: the browser
 *   refused the new tab, so show the link instead of leaving the admin
 */
export const openLabel = async (eventId) => {
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null; // the label page can't reach back into the admin
    const [result] = await fetchLabels([eventId]);
    const url = safeLabelUrl(result.url);
    if (url) {
        if (tab) tab.location.href = url;
        return { url, blocked: !tab };
    }
    if (tab) tab.close();
    throw new Error(result.error || "The courier has no label for this shipment yet. Try again in a minute.");
};

/** The link to a label whose new tab the browser blocked */
export const LabelBlocked = ({ url }) => (
    <div className="alert alert-warning py-2 mb-0" role="status">
        Your browser blocked the new tab. <a href={url} target="_blank" rel="noopener noreferrer">Open the label</a>
    </div>
);

/** The next 7 days as pickup dates: [{ value: YYYY-MM-DD, label: "Fri, 31 Oct" }] */
export const pickupDays = (now = new Date()) => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now.getTime() + i * DAY);
    return {
        value: d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
        label: `${i === 0 ? "Today, " : ""}${dayLabel(d, "Asia/Kolkata")}`,
    };
});

export function ShipmentProgress({ steps, step, tone }) {
    return (
        <ol className={`mtk-order-progress${tone === "blue" ? " is-blue" : ""}`} aria-label="Shipment progress">
            {steps.map((label, i) => (
                <li key={label} className={i <= step ? "is-done" : ""} aria-current={i === step ? "step" : undefined}>
                    <span className="bar" aria-hidden="true" />
                    <span className="lbl">{label}</span>
                </li>
            ))}
        </ol>
    );
}

export function ShippingSummary({ order, totals, pickup, courier, shippingKnown }) {
    const cod = isCod(order);
    const payment = cod
        ? (courier ? `COD – courier collects ${rupees(totals.total)}` : `COD – collect ${rupees(totals.total)} on delivery`)
        : order.payment_status === "paid" ? "Paid online" : "Payment pending";
    return (
        <div className="mtk-order-summary">
            <div>
                <div className="k">Pickup from</div>
                <div className="v">{pickup || "Not added yet"}</div>
                {shippingKnown && <Link to={SHIPPING_SETTINGS_PATH} className="small">Change in Shipping Settings</Link>}
            </div>
            <div>
                <div className="k">Charged to customer</div>
                <div className="v">{totals.shipping > 0 ? `${rupees(totals.shipping)} shipping` : "Free shipping"}</div>
            </div>
            <div>
                <div className="k">Payment</div>
                <div className={`v${cod ? " is-cod" : ""}`}>{payment}</div>
            </div>
        </div>
    );
}

const Notice = ({ tone, children, role }) => (
    <div className={`mtk-order-notice is-${tone}`} role={role || (tone === "red" ? "alert" : "status")}>{children}</div>
);

const PACKAGE_FIELDS = [
    { key: "weight", label: "Weight (kg)" },
    { key: "length", label: "Length (cm)" },
    { key: "width", label: "Width (cm)" },
    { key: "height", label: "Height (cm)" },
];

/** A courier store's order to ship: the courier's last error, the package, "Create shipment" and "Cancel order" */
export function CourierToShip({ order, providerLabel, initialPackage, onUpdated }) {
    const [pkg, setPkg] = useState(initialPackage);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const s = shipmentOf(order);

    // Filled again once the products' sizes have loaded, or for another order -
    // during render, so an edit made before they load isn't wiped out
    const initialKey = `${order._id}:${JSON.stringify(initialPackage)}`;
    const [filledFor, setFilledFor] = useState(initialKey);
    if (filledFor !== initialKey) {
        setFilledFor(initialKey);
        setPkg(initialPackage);
    }

    const complete = PACKAGE_FIELDS.every(({ key }) => Number(pkg[key]) > 0);

    const create = async () => {
        if (!complete) {
            setMessage({ type: "danger", text: "Enter the package weight and size first." });
            return;
        }
        setBusy(true);
        setMessage(null);
        try {
            await api.post(shipmentApi(order._id, "shipment"), {
                package: { weight_kg: Number(pkg.weight), length_cm: Number(pkg.length), width_cm: Number(pkg.width), height_cm: Number(pkg.height) },
            }, { timeout: SHIPMENT_TIMEOUT });
            onUpdated && onUpdated();
        } catch (error) {
            const code = error.response && error.response.data && error.response.data.code;
            // A courier error is saved on the order and shown once above when it
            // reloads, so it isn't repeated here; anything else is shown here
            const saved = ["auth", "unavailable", "rejected"].includes(code);
            setMessage(saved ? null : { type: "danger", text: apiError(error, `Couldn't reach ${providerLabel}. Please try again.`) });
            if (saved) onUpdated && onUpdated();
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        if (!window.confirm("Cancel this order? A cancelled order can't be shipped.")) return;
        setBusy(true);
        setMessage(null);
        try {
            const res = await api.put("/event/update-delivery-status", { event_id: order._id, ...businessScopeParams(), delivery_status: "cancelled" });
            if (res.data && res.data.status === "success") onUpdated && onUpdated();
            else setMessage({ type: "danger", text: "Couldn't cancel this order. Please try again." });
        } catch (error) {
            setMessage({ type: "danger", text: apiError(error, "Couldn't cancel this order. Please try again.") });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="courier-shipment">
            {s.last_error_code === "auth" && (
                <Notice tone="red">
                    <strong>Couldn't create the shipment – {providerLabel} rejected your saved API token.</strong>
                    <div className="mt-1">We won't keep retrying with a wrong token. Update it in Shipping Settings, then try again. New orders are paused until then.</div>
                    <div className="mtk-order-notice__actions">
                        <Link to={SHIPPING_SETTINGS_PATH} className="mtk-ship-btn mtk-ship-btn--red mtk-ship-btn--sm">Go to Shipping Settings</Link>
                        <button type="button" className="mtk-ship-btn mtk-ship-btn--outline-red mtk-ship-btn--sm" onClick={create} disabled={busy}>Try again</button>
                    </div>
                </Notice>
            )}
            {s.last_error_code === "unavailable" && (
                <Notice tone={s.next_retry_at ? "amber" : "red"}>
                    <strong>Couldn't create the shipment – {providerLabel} didn't respond.</strong>
                    <div className="mt-1">
                        {s.next_retry_at
                            ? `Retrying automatically (attempt ${(s.attempts || 0) + 1} of 5). You can also try again now.`
                            : "It didn't respond after 5 tries. Try again now."}
                    </div>
                    <div className="mtk-order-notice__actions">
                        <button type="button" className="mtk-ship-btn mtk-ship-btn--outline mtk-ship-btn--sm" onClick={create} disabled={busy}>Try again</button>
                    </div>
                </Notice>
            )}
            {s.last_error_code === "rejected" && (() => {
                const help = rejectionHelp(s.last_error, providerLabel);
                return (
                    <Notice tone="red">
                        <strong>Couldn't create the shipment – {help.reason}</strong>
                        <div className="mt-1">{help.advice}</div>
                        {help.partial && (
                            <div className="mt-1">{providerLabel} may have saved part of it: check your {providerLabel} account for a half-made shipment and cancel it before trying again.</div>
                        )}
                    </Notice>
                );
            })()}

            {s.last_error && !["auth", "unavailable", "rejected", ""].includes(s.last_error_code || "") && (
                <Notice tone="red">
                    <strong>Couldn't create the shipment – {s.last_error}</strong>
                    <div className="mt-1">Fix this, then try again.</div>
                </Notice>
            )}

            <fieldset className="mtk-order-package" disabled={busy}>
                <legend>Package</legend>
                <div className="mtk-order-package__fields">
                    {PACKAGE_FIELDS.map(({ key, label }) => (
                        <div key={key} className="form-group mb-0">
                            <label htmlFor={`pkg-${key}`}>{label}</label>
                            <input
                                id={`pkg-${key}`}
                                type="number"
                                min="0"
                                step="any"
                                inputMode="decimal"
                                className="form-control"
                                value={pkg[key]}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setPkg((prev) => ({ ...prev, [key]: value }));
                                }}
                            />
                        </div>
                    ))}
                </div>
                <p className="mtk-order-hint">
                    Filled from the product's saved size. Change it if you pack differently – the courier charges you by weight and size.
                </p>
            </fieldset>

            {message && (
                <div className={`alert alert-${message.type} py-2 mt-3 mb-0`} role={message.type === "danger" ? "alert" : "status"}>{message.text}</div>
            )}

            <div className="mtk-order-actions">
                <button type="button" className="mtk-ship-btn mtk-ship-btn--green mtk-ship-btn--lg" onClick={create} disabled={busy}>
                    {busy ? "Creating…" : "Create shipment"}
                </button>
                <span className="mtk-order-hint">Books it with {providerLabel} and gives you the AWB number and label.</span>
                <span className="mtk-order-actions__spacer" />
                <button type="button" className="mtk-order-cancel" onClick={cancel} disabled={busy}>Cancel order</button>
            </div>
        </div>
    );
}

/** Booked, not picked up yet: AWB, label, schedule pickup, cancel the shipment */
export function CourierBooked({ order, providerLabel, onUpdated }) {
    const s = shipmentOf(order);
    const days = pickupDays();
    const scheduled = s.status === "PICKUP_SCHEDULED" && s.pickup && s.pickup.date;
    const [day, setDay] = useState(days[1].value);
    const [slot, setSlot] = useState("10-14");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const run = async (fn, success) => {
        setBusy(true);
        setMessage(null);
        try {
            await fn();
            if (success) setMessage({ type: "success", text: success });
        } catch (error) {
            setMessage({ type: "danger", text: apiError(error, error.message || "Something went wrong. Please try again.") });
        } finally {
            setBusy(false);
        }
    };

    const [blockedLabel, setBlockedLabel] = useState("");
    const label = () => run(async () => {
        setBlockedLabel("");
        const { url, blocked } = await openLabel(order._id);
        if (blocked) setBlockedLabel(url);
    });
    const pickup = () => run(async () => {
        await api.post(shipmentApi(order._id, "pickup"), { date: day, slot });
        onUpdated && onUpdated();
    });
    const cancel = () => {
        if (!window.confirm(`Cancel this ${providerLabel} shipment? The order goes back to To ship.`)) return;
        run(async () => {
            await api.post(shipmentApi(order._id, "cancel"));
            onUpdated && onUpdated();
        });
    };

    return (
        <div className="mtk-order-stack" data-testid="courier-booked">
            <Notice tone="green" role="status">
                {scheduled ? (
                    <><strong>Pickup scheduled for {formatDay(s.pickup.date)}, {PICKUP_SLOTS[s.pickup.slot]}.</strong> Keep the parcel ready with the label on it.</>
                ) : (
                    <><strong>Shipment created with {providerLabel}.</strong> Print the label, stick it on the parcel, then schedule a pickup.</>
                )}
            </Notice>
            <div className="mtk-order-pair">
                <div><div className="k">AWB number</div><div className="v-lg">{s.awb}</div></div>
                <div>
                    <div className="k">Label</div>
                    <button type="button" className="mtk-ship-btn mtk-ship-btn--outline mtk-ship-btn--sm mt-1" onClick={label} disabled={busy}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
                        Download label (PDF)
                    </button>
                </div>
            </div>
            {blockedLabel && <LabelBlocked url={blockedLabel} />}
            <fieldset className="mtk-order-pickup" disabled={busy}>
                <legend>{scheduled ? "Change pickup" : "Schedule pickup"}</legend>
                <div className="mtk-order-pair">
                    <div className="form-group mb-0">
                        <label htmlFor="pickup-date">Pickup date</label>
                        <select id="pickup-date" className="form-control" value={day} onChange={(e) => setDay(e.target.value)}>
                            {days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="pickup-slot">Time slot</label>
                        <select id="pickup-slot" className="form-control" value={slot} onChange={(e) => setSlot(e.target.value)}>
                            {Object.entries(PICKUP_SLOTS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                        </select>
                    </div>
                </div>
                <button type="button" className="mtk-ship-btn mtk-ship-btn--green mt-3" onClick={pickup}>
                    {scheduled ? "Change pickup" : "Schedule pickup"}
                </button>
            </fieldset>
            {message && <div className={`alert alert-${message.type} py-2 mb-0`} role={message.type === "danger" ? "alert" : "status"}>{message.text}</div>}
            <div className="mtk-order-cancel-row">
                <span className="mtk-order-hint m-0">Changed your mind? You can cancel until the courier picks it up.</span>
                <button type="button" className="mtk-ship-btn mtk-ship-btn--outline-red mtk-ship-btn--sm" onClick={cancel} disabled={busy}>Cancel shipment</button>
            </div>
        </div>
    );
}

/** Picked up and on: AWB, expected delivery, label, the courier's tracking page, tracking updates */
export function CourierMoving({ order, providerLabel, onUpdated }) {
    const s = shipmentOf(order);
    const [message, setMessage] = useState(null);
    const [blockedLabel, setBlockedLabel] = useState("");
    const [closing, setClosing] = useState(false);
    const quietDays = daysSinceCourierUpdate(order);
    const stale = !["DELIVERED", "RTO_DELIVERED"].includes(s.status) && quietDays >= STALE_DAYS;
    const close = async () => {
        if (!window.confirm(`Stop following this ${providerLabel} shipment and update the order yourself? ${providerLabel} updates won't change this order any more.`)) return;
        setClosing(true);
        setMessage(null);
        try {
            await api.post(shipmentApi(order._id, "close"));
            onUpdated && onUpdated();
        } catch (error) {
            setMessage(apiError(error, "Couldn't close this shipment. Please try again."));
        } finally {
            setClosing(false);
        }
    };
    const events = [...(s.events || [])].sort((a, b) => new Date(b.time) - new Date(a.time));
    const eta = s.expected_delivery || order.estimated_delivery_date;
    const label = async () => {
        setMessage(null);
        setBlockedLabel("");
        try {
            const { url, blocked } = await openLabel(order._id);
            if (blocked) setBlockedLabel(url);
        } catch (error) {
            setMessage(apiError(error, error.message));
        }
    };
    return (
        <div className="mtk-order-stack" data-testid="courier-moving">
            <div className="mtk-order-triple">
                <div><div className="k">AWB number</div><div className="v-lg">{s.awb}</div></div>
                <div><div className="k">{s.status === "DELIVERED" ? "Delivered" : "Expected delivery"}</div><div className="v">{s.status === "DELIVERED" ? formatShort(order.actual_delivery_date) : formatDay(eta) || "—"}</div></div>
                <div className="mtk-order-btns">
                    <button type="button" className="mtk-ship-btn mtk-ship-btn--outline mtk-ship-btn--sm" onClick={label}>Label</button>
                    {order.courier_tracking_url && (
                        <a href={order.courier_tracking_url} target="_blank" rel="noopener noreferrer" className="mtk-ship-btn mtk-ship-btn--outline mtk-ship-btn--sm">
                            Track on {providerLabel} <span aria-hidden="true">↗</span>
                        </a>
                    )}
                </div>
            </div>
            {message && <div className="alert alert-danger py-2 mb-0" role="alert">{message}</div>}
            {blockedLabel && <LabelBlocked url={blockedLabel} />}
            {stale && (
                <Notice tone="amber" role="status">
                    <strong>No update from {providerLabel} for {quietDays} days.</strong> Check with {providerLabel}. If the parcel is lost or your account has closed, you can stop following it and update this order yourself.
                    <div className="mtk-order-notice__actions">
                        <button type="button" className="mtk-ship-btn mtk-ship-btn--outline mtk-ship-btn--sm" onClick={close} disabled={closing} data-testid="close-shipment">Stop following and update by hand</button>
                    </div>
                </Notice>
            )}
            {s.status_detail && ["FAILED_DELIVERY", "RTO_INITIATED", "RTO_DELIVERED"].includes(s.status) && (
                <Notice tone={s.status === "FAILED_DELIVERY" ? "amber" : "red"}>
                    <strong>{COURIER_CHIPS[s.status].label}:</strong> {s.status_detail}
                </Notice>
            )}
            <div>
                <h3 className="mtk-order-h3">Tracking updates</h3>
                {events.length ? (
                    <ol className="mtk-order-timeline">
                        {events.map((e, i) => (
                            <li key={`${e.raw_status}-${e.time}-${i}`} className={i === 0 ? "is-latest" : ""}>
                                <div className="font-weight-bold">{COURIER_CHIPS[e.status] ? COURIER_CHIPS[e.status].label : e.status === "CREATED" ? "Shipment created" : e.status === "CANCELLED" ? "Shipment cancelled" : e.raw_status || e.status}</div>
                                <div className="mtk-order-muted small">{[formatShort(e.time), e.location, e.note].filter(Boolean).join(" · ")}</div>
                                <div className="mtk-order-faint">{e.by === "seller" ? "By you" : e.raw_status ? `Courier status: "${e.raw_status}"` : ""}</div>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="mtk-order-hint">No updates from {providerLabel} yet.</p>
                )}
                <p className="mtk-order-hint">Cancel isn't available after pickup. Updates come from {providerLabel} automatically.</p>
            </div>
        </div>
    );
}

// Couriers a self-ship seller picks from (any name can be typed)
export const SELF_SHIP_COURIERS = ["ST Courier", "The Professional Couriers", "DTDC", "India Post", "Shree Maruti Courier", "Delhivery", "Blue Dart", "Own delivery"];

const MANUAL_NEXT = {
    shipped: ["in_transit", "out_for_delivery", "delivered", "failed_delivery", "returned"],
    in_transit: ["out_for_delivery", "delivered", "failed_delivery", "returned"],
    out_for_delivery: ["delivered", "failed_delivery", "returned"],
    failed_delivery: ["out_for_delivery", "delivered", "returned"],
};
const MANUAL_LABELS = {
    in_transit: "In transit", out_for_delivery: "Out for delivery", delivered: "Delivered",
    failed_delivery: "Delivery failed", returned: "Returned",
};

const toDateInput = (value) => {
    const d = date(value) || new Date();
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
};

/**
 * A Manual / self-ship order: the courier name, tracking number, optional
 * link and the day it was sent; then its status (In transit … Returned).
 */
export function ManualShipping({ order, onUpdated }) {
    const status = order.current_tracking_status || "ordered";
    const dispatched = !!MANUAL_NEXT[status] || ["delivered", "returned"].includes(status);
    const fromOrder = () => ({
        courier: order.courier_service || "",
        tracking: order.tracking_number || "",
        link: order.courier_tracking_url || "",
        shippedOn: toDateInput(order.shipped_on),
        next: (MANUAL_NEXT[status] || [])[0] || "",
    });
    const [form, setForm] = useState(fromOrder);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const orderKey = `${order._id}:${status}`;
    const [formFor, setFormFor] = useState(orderKey);
    if (formFor !== orderKey) {
        setFormFor(orderKey);
        setForm(fromOrder());
    }

    const send = async (fields, success) => {
        setBusy(true);
        setMessage(null);
        try {
            const res = await api.put("/event/update-delivery-status", { event_id: order._id, ...businessScopeParams(), ...fields });
            if (res.data && res.data.status === "success") {
                setErrors({});
                setMessage({ type: "success", text: success });
                onUpdated && onUpdated();
            } else {
                setMessage({ type: "danger", text: "Couldn't update this order. Please try again." });
            }
        } catch (error) {
            const data = (error.response && error.response.data) || {};
            if (Array.isArray(data.fields)) setErrors(Object.fromEntries(data.fields.map((f) => [f, data.message])));
            setMessage({ type: "danger", text: apiError(error, "Couldn't update this order. Please try again.") });
        } finally {
            setBusy(false);
        }
    };

    const details = () => ({
        courier_service: form.courier.trim(),
        tracking_number: form.tracking.trim(),
        courier_tracking_url: form.link.trim(),
        shipped_on: form.shippedOn,
    });

    const markShipped = () => {
        const missing = {};
        if (!form.courier.trim()) missing.courier_service = "Enter the courier name";
        if (!form.tracking.trim()) missing.tracking_number = "Enter the tracking number";
        if (Object.keys(missing).length) {
            setErrors(missing);
            return;
        }
        send({ delivery_status: "shipped", ...details() }, "Marked as shipped. The customer has been told how to track it.");
    };

    const saveDetails = () => send(details(), "Shipping details saved.");
    const updateStatus = () => {
        if (form.next === "returned" && !window.confirm("Mark this order as returned? It can't be changed after that.")) return;
        send({ delivery_status: form.next }, `Status updated to ${MANUAL_LABELS[form.next]}.`);
    };
    const cancel = () => {
        if (!window.confirm("Cancel this order? A cancelled order can't be shipped.")) return;
        send({ delivery_status: "cancelled" }, "Order cancelled.");
    };

    const set = (fields) => {
        setForm((prev) => ({ ...prev, ...fields }));
        setErrors({});
    };
    const err = (field) => errors[field] && <div className="invalid-feedback d-block" id={`${field}-error`}>{errors[field]}</div>;
    const final = status === "delivered" || status === "returned";

    return (
        <div className="mtk-order-stack" data-testid="order-shipping">
            {!dispatched && (
                <div className="mtk-order-infobox">
                    Your store uses <strong>Manual / self-ship</strong>. Pack and send it with any courier, then add the details here so the customer can track it.
                </div>
            )}
            <fieldset className="mtk-order-manual" disabled={busy || final}>
                <legend className="sr-only">Shipping details</legend>
                <div className="mtk-order-pair">
                    <div className="form-group mb-0">
                        <label htmlFor="ship-courier">Courier name</label>
                        <input id="ship-courier" list="ship-courier-list" className={`form-control${errors.courier_service ? " is-invalid" : ""}`}
                            value={form.courier} maxLength={100} onChange={(e) => set({ courier: e.target.value })}
                            aria-describedby={errors.courier_service ? "courier_service-error" : undefined} />
                        <datalist id="ship-courier-list">{SELF_SHIP_COURIERS.map((c) => <option key={c} value={c} />)}</datalist>
                        {err("courier_service")}
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="ship-tracking-id">Tracking number</label>
                        <input id="ship-tracking-id" className={`form-control${errors.tracking_number ? " is-invalid" : ""}`}
                            placeholder="As printed on the courier receipt" value={form.tracking} maxLength={100}
                            onChange={(e) => set({ tracking: e.target.value })}
                            aria-describedby={errors.tracking_number ? "tracking_number-error" : undefined} />
                        {err("tracking_number")}
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="ship-tracking-url">Tracking link <span className="mtk-order-muted font-weight-normal">(optional)</span></label>
                        <input id="ship-tracking-url" type="url" className={`form-control${errors.courier_tracking_url ? " is-invalid" : ""}`}
                            placeholder="https://" value={form.link} onChange={(e) => set({ link: e.target.value })}
                            aria-describedby={errors.courier_tracking_url ? "courier_tracking_url-error" : undefined} />
                        {err("courier_tracking_url")}
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="ship-shipped-on">Shipped on</label>
                        <input id="ship-shipped-on" type="date" className={`form-control${errors.shipped_on ? " is-invalid" : ""}`}
                            value={form.shippedOn} max={toDateInput(new Date())} onChange={(e) => set({ shippedOn: e.target.value })} />
                        {err("shipped_on")}
                    </div>
                </div>
            </fieldset>

            {message && <div className={`alert alert-${message.type} py-2 mb-0`} role={message.type === "danger" ? "alert" : "status"}>{message.text}</div>}

            {!dispatched && (
                <div className="mtk-order-actions mt-0">
                    <button type="button" className="mtk-ship-btn mtk-ship-btn--green mtk-ship-btn--lg" onClick={markShipped} disabled={busy}>Mark as shipped</button>
                    <span className="mtk-order-hint m-0">After this you can update the status: In transit, Out for delivery, Delivered, Delivery failed or Returned.</span>
                    <span className="mtk-order-actions__spacer" />
                    <button type="button" className="mtk-order-cancel" onClick={cancel} disabled={busy}>Cancel order</button>
                </div>
            )}

            {dispatched && !final && (
                <div className="mtk-order-status-row">
                    <button type="button" className="mtk-ship-btn mtk-ship-btn--outline" onClick={saveDetails} disabled={busy}>Save details</button>
                    <span className="mtk-order-actions__spacer" />
                    <label htmlFor="ship-next" className="m-0 font-weight-bold">Status</label>
                    <select id="ship-next" className="form-control mtk-order-select" value={form.next} onChange={(e) => set({ next: e.target.value })} disabled={busy}>
                        {MANUAL_NEXT[status].map((s) => <option key={s} value={s}>{MANUAL_LABELS[s]}</option>)}
                    </select>
                    <button type="button" className="mtk-ship-btn mtk-ship-btn--green" onClick={updateStatus} disabled={busy}>Update status</button>
                </div>
            )}
            {final && <p className="mtk-order-hint m-0" data-testid="order-completed">This order is {status === "delivered" ? "delivered and completed" : "returned"}, so its status can't change.</p>}
        </div>
    );
}

/**
 * Asks the courier for this order's tracking when it's moving and wasn't
 * checked in the last 10 minutes; reloads the order if anything changed.
 */
export function useFreshTracking(order, onUpdated) {
    const asked = useRef(null);
    useEffect(() => {
        // A Super Admin has no seller session for the courier APIs (isSellerSession)
        if (!order || !isSellerSession()) return;
        const s = shipmentOf(order);
        const moving = hasShipment(order) && !["DELIVERED", "RTO_DELIVERED"].includes(s.status);
        const synced = date(s.last_synced_at);
        if (!moving || asked.current === order._id || (synced && Date.now() - synced.getTime() < 10 * 60000)) return;
        asked.current = order._id;
        api.get(shipmentApi(order._id, "tracking"))
            .then((res) => {
                if (res && res.data && res.data.result && res.data.result.status !== s.status) onUpdated && onUpdated();
            })
            .catch(() => {});
    }, [order, onUpdated]);
}

export function CustomerCard({ order }) {
    const name = order.event_name || [order.first_name, order.last_name].filter(Boolean).join(" ");
    const address = customerAddress(order);
    return (
        <section className="mtk-order-card" aria-labelledby="order-customer-title">
            <h2 id="order-customer-title">Customer</h2>
            <div className="font-weight-bold">{name}</div>
            {order.phone_number && <div className="mtk-order-muted">{order.phone_number}</div>}
            {address && <div className="mtk-order-muted mt-1">{address}</div>}
        </section>
    );
}

export function ItemsCard({ order, lines, totals, shippingLabel }) {
    const status = order.current_tracking_status;
    return (
        <section className="mtk-order-card" aria-labelledby="order-items-title">
            <h2 id="order-items-title">Items</h2>
            <ul className="mtk-order-items">
                {lines.map((line) => (
                    <li key={line.id}>
                        {line.url ? <img src={line.url} alt={line.name} /> : <span className="mtk-order-items__ph" aria-hidden="true" />}
                        <div className="mtk-order-items__info">
                            <div className="font-weight-bold">{line.name}</div>
                            <div className="mtk-order-muted small">Qty {line.qty}{line.kg != null ? ` · ${formatKg(line.kg)} kg` : ""}</div>
                            {line.variants.length > 0 && (
                                <div className="mtk-order-muted small">{line.variants.map((v) => `${v.type}: ${v.value}`).join(" · ")}</div>
                            )}
                        </div>
                        <div className="font-weight-bold">{rupees(line.price * line.qty)}</div>
                    </li>
                ))}
            </ul>
            <div className="mtk-order-totals">
                <div><span>Items</span><span>{rupees(totals.items)}</span></div>
                <div><span>{shippingLabel}</span><span>{rupees(totals.shipping)}</span></div>
                <div className="is-total"><span>Total</span><span>{rupees(totals.total)}</span></div>
                <div className="small">
                    <span className="mtk-order-muted">Payment</span>
                    <span className={isCod(order) ? "is-cod" : ""}>
                        {isCod(order)
                            ? (status === "delivered" ? "COD · collected" : "COD · to be collected")
                            : order.payment_status === "paid" ? "Paid online" : "Pending"}
                    </span>
                </div>
            </div>
        </section>
    );
}
